import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Badge, Button, Card, EmptyState, ErrorAlert, Pagination, StatCard } from '../components/ui';
import { usePagination } from '../lib/usePagination';
import { NieboGuardArticlePreview } from './NieboGuardArticlePreview';

type Any = Record<string, any>;

const FORMATS = ['EXPLAINER', 'ATLAS', 'SCENARIO', 'TIMELINE', 'MAP_STORY', 'SIMULATION', 'COMPARISON', 'QUIZ', 'MYTH_FACT', 'CASE_STUDY'];
const STATUSES = ['IDEA', 'DRAFT', 'VALIDATED', 'REVIEW', 'APPROVED', 'PUBLISHED', 'NEEDS_UPDATE', 'ARCHIVED'];

function fmt(value: unknown): string {
  if (!value) return '—';
  const time = Date.parse(String(value));
  if (!Number.isFinite(time)) return String(value).slice(0, 40);
  return new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', dateStyle: 'short', timeStyle: 'short' }).format(new Date(time));
}

export function NieboGuardArticleStudioSection() {
  const [items, setItems] = useState<Any[]>([]);
  const paged = usePagination(items, 12);
  const [format, setFormat] = useState('');
  const [status, setStatus] = useState('');
  const [onlyPilots, setOnlyPilots] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [message, setMessage] = useState('');
  const [lastArticle, setLastArticle] = useState<string | null>(null);
  const [preview, setPreview] = useState<Any | null>(null);

  const load = useCallback(async () => {
    setError(null);
    const result = await api.nieboguard.articleBacklog({
      format: format || undefined,
      status: status || undefined,
      pilot: onlyPilots ? true : undefined,
    });
    setItems(result.items ?? []);
  }, [format, status, onlyPilots]);

  useEffect(() => { load().catch(setError); }, [load]);

  const act = async (key: string, action: () => Promise<Any>, success: string) => {
    setBusy(key); setError(null); setMessage('');
    try {
      const result = await action();
      if (result?.article?.id) setLastArticle(result.article.id);
      setMessage(result?.article?.id ? `${success} ID: ${result.article.id}` : success);
      await load();
    } catch (err) { setError(err); }
    finally { setBusy(null); }
  };

  const openPreview = async (item: Any) => {
    setBusy(`preview-${item.id}`); setError(null);
    try {
      const result = await api.nieboguard.articleBacklogPreview(item.id);
      if (!result.article) { setMessage('Najpierw utwórz szkic artykułu.'); return; }
      setPreview(result.article);
    } catch (err) { setError(err); }
    finally { setBusy(null); }
  };

  const pilots = items.filter((item) => item.pilot).length;
  const drafts = items.filter((item) => item.articleId).length;
  const safeCritical = items.filter((item) => item.safetyClassification === 'SAFETY_CRITICAL').length;

  return <div className="nb-list">
    {error ? <ErrorAlert error={error} /> : null}
    {message ? <div className="alert alert--success">{message}</div> : null}

    <div className="nb-hero">
      <div className="nb-row nb-row--between">
        <div>
          <h2>Baza wiedzy 2.0 — Article Studio</h2>
          <p>Wspólny renderer dla dziewięciu formatów artykułów. Backlog ma 45 tematów; dziewięć oznaczono jako pilotażowe. Utworzenie szkicu nie publikuje treści: artykuł pozostaje w statusie DRAFT i wymaga redakcyjnej akceptacji.</p>
        </div>
        <Badge tone="info">45 tematów · 9 pilotów</Badge>
      </div>
      <div className="nb-row">
        <Badge tone="success">Walidacja bloków P0</Badge>
        <Badge tone="neutral">Bez wykonywania kodu z LLM</Badge>
        <Badge tone="warning">Publikacja tylko po akceptacji</Badge>
      </div>
    </div>

    <div className="nb-grid nb-grid--stats">
      <StatCard label="Tematy w widoku" value={items.length} />
      <StatCard label="Pilotażowe" value={pilots} />
      <StatCard label="Utworzone szkice" value={drafts} />
      <StatCard label="Safety-critical" value={safeCritical} />
    </div>

    <Card title="Kolejka redakcyjna" actions={<div className="btn-row">
      <Button size="sm" variant="ghost" onClick={() => load().catch(setError)} disabled={busy !== null}>Odśwież</Button>
      <Button size="sm" variant="primary" disabled={busy !== null} onClick={() => act('pilots', () => api.nieboguard.materializePilotArticles(), 'Utworzono szkice pilotażowe (lub zwrócono istniejące).')}>
        {busy === 'pilots' ? 'Tworzę szkice…' : 'Utwórz 9 szkiców pilotażowych'}
      </Button>
    </div>}>
      <div className="nb-row" style={{ marginBottom: 12 }}>
        <label className="field"><span>Format</span>
          <select value={format} onChange={(event) => setFormat(event.target.value)}>
            <option value="">Wszystkie</option>
            {FORMATS.map((value) => <option key={value} value={value}>{value}</option>)}
          </select>
        </label>
        <label className="field"><span>Status</span>
          <select value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">Wszystkie</option>
            {STATUSES.map((value) => <option key={value} value={value}>{value}</option>)}
          </select>
        </label>
        <label className="nb-check"><input type="checkbox" checked={onlyPilots} onChange={(event) => setOnlyPilots(event.target.checked)} /> Tylko piloty</label>
      </div>

      {!items.length ? <EmptyState title="Brak tematów" hint="Uruchom migrację 0140 lub utwórz temat przez API." /> :
        <div className="nb-scroll"><table className="table">
          <thead><tr><th>Kod</th><th>Tytuł</th><th>Format</th><th>Kategoria</th><th>Priorytet</th><th>Status</th><th>Szkic</th><th /></tr></thead>
          <tbody>{paged.pageRows.map((item) => <tr key={item.id}>
            <td><code>{item.code}</code>{item.pilot ? <div><Badge tone="info">PILOT</Badge></div> : null}</td>
            <td><strong>{item.title}</strong><div className="nb-item__meta">{item.safetyClassification}</div></td>
            <td>{item.format}</td>
            <td>{item.category}</td>
            <td>{item.priority}</td>
            <td><Badge tone={item.status === 'DRAFT' ? 'warning' : item.status === 'APPROVED' ? 'success' : 'neutral'}>{item.status}</Badge></td>
            <td>{item.articleId ? <><code>{item.articleId}</code><div className="nb-item__meta">{fmt(item.updatedAt)}</div></> : <span className="nb-muted">brak</span>}</td>
            <td><div className="btn-row"><Button size="sm" variant={item.pilot ? 'primary' : 'ghost'} disabled={busy !== null} onClick={() => act(`mat-${item.id}`, () => api.nieboguard.materializeArticleBacklog(item.id), `Szkic ${item.code} gotowy do redakcji.`)}>
              {busy === `mat-${item.id}` ? 'Tworzę…' : item.articleId ? 'Otwórz / odśwież' : 'Utwórz szkic'}
            </Button><Button size="sm" variant="ghost" disabled={busy !== null || !item.articleId} onClick={() => openPreview(item)}>{busy === `preview-${item.id}` ? 'Ładuję…' : 'Podgląd'}</Button></div></td>
          </tr>)}</tbody>
        </table><Pagination page={paged.page} pageSize={paged.pageSize} total={paged.total} onChange={paged.setPage} /></div>}
    </Card>

    <Card title="Co dalej z gotowym szkicem?">
      <ol className="nb-steps">
        <li>Sprawdź źródła, liczby i daty — szczególnie w tematach SAFETY_CRITICAL.</li>
        <li>Uzupeń grafiki AI lub schematy SVG; każdy blok ma wersję i walidację.</li>
        <li>Ustaw status VALIDATED → REVIEW i zatwierdź w panelu redakcyjnym.</li>
        <li>Wyślij artykuł do NieboGuard przez istniejący most treści (zakładka Treści / SEO).</li>
        <li>Nie publikuj automatycznie. Dzienny limit publikacji na platformę pozostaje bez zmian.</li>
      </ol>
      {lastArticle ? <p className="nb-stat-note">Ostatnio utworzony szkic: <code>{lastArticle}</code>. Przejdź do zakładki „Treści / SEO”, aby go zatwierdzić i wysłać.</p> : null}
    </Card>
    {preview ? <NieboGuardArticlePreview article={preview} onClose={() => setPreview(null)} /> : null}
  </div>;
}
